import { createContext, useContext, useState, useCallback } from 'react';

const AccountModalContext = createContext();

/**
 * Custom hook to access the AccountModalContext.
 *
 * This hook provides access to the context value for managing the visibility
 * of account-related modals, such as login and upgrade modals. It allows
 * components to consume the context and utilize the modal visibility state
 * and toggle functions.
 *
 * @returns {Object} The context value containing state and functions related
 *                   to account modal visibility.
 */

export const useModal = () => {
    return useContext(AccountModalContext);
};

/**
 * Provides a context for managing the visibility of the login and upgrade modals.
 *
 * This context is used by the login modal and the upgrade modal to determine
 * whether they should be visible or not. It also provides functions to toggle the
 * visibility of the modals.
 *
 * @param {Object} props - props object
 * @param {React.ReactNode} props.children - children components
 *
 * @returns {React.ReactNode} - the children components wrapped in the context provider
 */

export const AccountModalProvider = ({ children }) => {
    const [showModalAccount, setShowModalAccount] = useState(false); // login modal
    const [showUpgradeModal, setShowUpgradeModal] = useState(false); // upgrade modal

    const toggleModalAccount = useCallback(() => {
        setShowModalAccount((prev) => !prev);
    }, []);

    const toggleUpgradeModal = useCallback(() => {
        setShowUpgradeModal((prev) => !prev);
    }, []);

    const value = {
        showModalAccount,
        toggleModalAccount,
        showUpgradeModal,
        toggleUpgradeModal,
    };

    return (
        <AccountModalContext.Provider value={value}>
            {children}
        </AccountModalContext.Provider>
    );
};
